import React, { ChangeEvent, useState } from "react";
import { accountApis } from "@payping/api";
import {
  Button,
  Flex,
  Input,
  LegacyCard,
  LegacyForm,
  Modal,
  Space,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { useNavigate } from "react-router-dom";

import { CheckCircleOutlined, LeftOutlined, LockOutlined } from "@payping/icons";
import { OtpCode } from "@/components/OtpCode";
import { DashboardRoutes } from "@/router/constant";
import { useAppTranslation } from "@/utils/useAppTranslation";

interface PasswordFormType {
  newPassword: string;
  confirmPassword: string;
  otpCode: string;
}

const PasswordSection: React.FC = () => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<PasswordFormType>();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [isOtpValid, setIsOtpValid] = useState(false);

  const [password, setPassword] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState({
    hasNumber: false,
    hasLetter: false,
    hasUpperCase: false,
    minLength: false,
  });

  const validatePassword = (value: string) => {
    const hasNumber = /\d/.test(value); // شامل عدد
    const hasLetter = /[a-zA-Z]/.test(value); // شامل حروف
    const hasUpperCase = /[A-Z]/.test(value); // حداقل یک حرف بزرگ
    const minLength = value.length >= 6; // حداقل طول

    setIsPasswordValid({ hasNumber, hasLetter, hasUpperCase, minLength });
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPassword(value);
    validatePassword(value);
  };

  const [{ loading }, handleEdit] = useMessagedAsyncFn(
    async (values: PasswordFormType) => {
      try {
        await accountApis.changePasswordAsyncWithRequestModelPut(values);
        setOpenModal(false);
        navigate(DashboardRoutes.profile.password.url);
      } catch {
        /* empty */
      }
    },
    [navigate],
    undefined,
    { useProgress: true },
  );

  return (
    <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
      <Typography.Title level={4} className={"tw:font-medium"}>
        {t("profile.password")}
      </Typography.Title>
      <LegacyCard.Ant onClick={() => setOpenModal(true)}>
        <div className={"tw:flex tw:justify-between"}>
          <Space>
            <LockOutlined width={16} />
            <Typography.Title level={5} className={"tw:m-0!"}>
              {t("profile.changePassword")}
            </Typography.Title>
          </Space>
          <LeftOutlined width={16} />
        </div>
      </LegacyCard.Ant>
      <Modal
        title={t("profile.changePassword")}
        open={openModal}
        footer={
          <Flex gap={"small"}>
            <Button type={"default"} onClick={() => setOpenModal(false)}>
              {t("return")}
            </Button>
            <Button onClick={() => form.submit()} type={"primary"} disabled={!isOtpValid}>
              {t("save")}
            </Button>
          </Flex>
        }
        okButtonProps={{ loading }}
      >
        <LegacyForm<PasswordFormType> onFinish={handleEdit} form={form as any} layout={"vertical"}>
          <LegacyForm.Item name={"newPassword"} label={t("profile.pass")} rules={[{ required: true }]}>
            <Input.Password
              size={"large"}
              className="en-font"
              style={{ direction: "ltr" }}
              value={password}
              placeholder={t("profile.changePassPlaceHolder")}
              onChange={handlePasswordChange}
            />
          </LegacyForm.Item>
          <LegacyForm.Item
            name={"confirmPassword"}
            label={t("settings.changePassword.repeat")}
            rules={[{ required: true }]}
          >
            <Input.Password
              size={"large"}
              className="en-font"
              style={{ direction: "ltr" }}
              placeholder={t("profile.changePassPlaceHolder")}
            />
          </LegacyForm.Item>
          <Flex vertical gap={"large"}>
            <Flex vertical gap={"small"}>
              <Flex gap={"small"}>
                {isPasswordValid.hasLetter && isPasswordValid.hasNumber ? (
                  <CheckCircleOutlined color={"green"} width={16} />
                ) : (
                  <CheckCircleOutlined color={"gray"} width={16} />
                )}

                <Typography.Text
                  type={isPasswordValid.hasLetter && isPasswordValid.hasNumber ? "success" : "secondary"}
                >
                  {t("profile.containsLettersAndNumbers")}
                </Typography.Text>
              </Flex>
              <Flex gap={"small"}>
                {isPasswordValid.minLength ? (
                  <CheckCircleOutlined color={"green"} width={16} />
                ) : (
                  <CheckCircleOutlined color={"gray"} width={16} />
                )}
                <Typography.Text type={isPasswordValid.minLength ? "success" : "secondary"}>
                  {t("profile.atLeast6Letters")}
                </Typography.Text>
              </Flex>
              <Flex gap={"small"}>
                {isPasswordValid.hasUpperCase ? (
                  <CheckCircleOutlined color={"green"} width={16} />
                ) : (
                  <CheckCircleOutlined color={"gray"} width={16} />
                )}
                <Typography.Text type={isPasswordValid.hasUpperCase ? "success" : "secondary"}>
                  {t("profile.atLeastOneCapitalEnglishLetter")}
                </Typography.Text>
              </Flex>
            </Flex>
            <OtpCode form={form} onVerify={(isVerified) => setIsOtpValid(isVerified)} />
          </Flex>
        </LegacyForm>
      </Modal>
    </Space>
  );
};

export default PasswordSection;
